import { useEffect, useState } from "react";
import { InlineWidget } from "react-calendly";
import axios from "axios";

import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { slackHook } from "src/utils/snug/slackHooks";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import {
  clearStarterPayContext,
  readStarterPayContext,
} from "src/sections/starter-pay/context";
import { SELF_SERVE_KEY } from "src/hooks/use-onboarding-state";

type RepChoice = "dan" | "gabrielle";

const REP_ASSET_BASE =
  "https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files";

const REPS: Record<
  RepChoice,
  { name: string; calendlyUrl: string; avatarUrl: string }
> = {
  dan: {
    name: "Dan",
    calendlyUrl:
      "https://calendly.com/financial-professionals-snug/snug-onboarding-dan",
    avatarUrl: `${REP_ASSET_BASE}/rep_dan.png`,
  },
  gabrielle: {
    name: "Gabrielle",
    calendlyUrl:
      "https://calendly.com/financial-professionals-snug/snug-onboarding-gabrielle",
    avatarUrl: `${REP_ASSET_BASE}/rep_gabrielle.png`,
  },
};

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5.5 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function OnboardingStep() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [repDialogOpen, setRepDialogOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState<RepChoice | null>(null);

  useEffect(() => {
    const ctx = readStarterPayContext();
    if (ctx?.name && ctx?.email) {
      setName(ctx.name);
      setEmail(ctx.email);
      return;
    }
    const token = localStorage.getItem("s_at");
    if (!token) return;
    const apiBase = process.env.REACT_APP_SNUG_API_URL || "";
    axios
      .get(`${apiBase}/v3/user-data/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { expand: "professional_group_role" },
      })
      .then(({ data }) => {
        setName(data.data?.full_name || "");
        setEmail(data.data?.contact_email || "");
      })
      .catch(() => {
        /* prefill is best-effort */
      });
  }, []);

  useEffect(() => {
    if (!repDialogOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setRepDialogOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [repDialogOpen]);

  const handlePickRep = (rep: RepChoice) => {
    setSelectedRep(rep);
    setRepDialogOpen(false);
    slackHook(
      `📞 STARTER PAY — Onboarding choice: WITH REP (${REPS[rep].name})\nName: ${name}\nEmail: ${email}`,
    );
  };

  const handleSelfServe = () => {
    slackHook(
      `🚀 STARTER PAY — Onboarding choice: SELF\nName: ${name}\nEmail: ${email}`,
    );
    localStorage.setItem(SELF_SERVE_KEY, "true");
    clearStarterPayContext();
    router.push(paths.dashboard.upgrade);
  };

  const handleSkip = () => {
    slackHook(
      `⏭ STARTER PAY — Onboarding choice: SKIP\nName: ${name}\nEmail: ${email}`,
    );
    clearStarterPayContext();
    router.push(paths.dashboard.index);
  };

  return (
    <>
      <SignupStepper currentStep="onboarding" />

      <div className="sp-card">
        <div className="sp-card-head">
          <h2 className="sp-section-title">Pick your onboarding path</h2>
          <p className="sp-section-help">
            Choose how you'd like to get up and running. You can change your
            mind any time from your dashboard.
          </p>
        </div>

        <div className="sp-option-stack">
          <button
            type="button"
            className={`sp-option-card${selectedRep ? " is-selected" : ""}`}
            onClick={() => setRepDialogOpen(true)}
          >
            <div className="sp-option-card-body">
              <span className="sp-option-card-title">
                Onboard with a Snug Rep
              </span>
              <span className="sp-option-card-desc">
                {selectedRep ? (
                  <>
                    Booking with <strong>{REPS[selectedRep].name}</strong>
                    <span style={{ opacity: 0.6 }}> · click to change rep</span>
                  </>
                ) : (
                  "Book a 1-on-1 walkthrough. We'll help you go live, integrate with your site, and get the most out of the platform."
                )}
              </span>
            </div>
            <span className="sp-option-card-arrow">
              <ArrowIcon />
            </span>
          </button>

          <button
            type="button"
            className="sp-option-card"
            onClick={handleSelfServe}
          >
            <div className="sp-option-card-body">
              <span className="sp-option-card-title">Set up on my own</span>
              <span className="sp-option-card-desc">
                Walk through setup yourself by adding DNS records and uploading
                your logo directly from your dashboard.
              </span>
            </div>
            <span className="sp-option-card-arrow">
              <ArrowIcon />
            </span>
          </button>
        </div>

        {selectedRep && (
          <div className="sp-calendly">
            <InlineWidget
              key={selectedRep}
              url={REPS[selectedRep].calendlyUrl}
              prefill={{ email, name }}
              styles={{ height: 720 }}
            />
          </div>
        )}
      </div>

      <button type="button" className="sp-skip-link" onClick={handleSkip}>
        Skip for now
      </button>

      {repDialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="rep-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRepDialogOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13, 13, 18, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "48px 32px 32px",
              maxWidth: 520,
              width: "100%",
              position: "relative",
              boxShadow: "0 24px 64px rgba(0, 0, 0, 0.25)",
            }}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setRepDialogOpen(false)}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                borderRadius: 8,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#5A5A68",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <h2
              id="rep-dialog-title"
              style={{
                textAlign: "center",
                margin: "0 0 8px",
                fontFamily: "var(--sp-serif)",
                fontSize: 32,
                fontWeight: 400,
                lineHeight: 1.15,
                color: "#0D0D12",
              }}
            >
              Pick your Snug rep
            </h2>
            <p
              style={{
                textAlign: "center",
                margin: "0 0 28px",
                fontSize: 14,
                color: "#5A5A68",
              }}
            >
              They'll guide you through onboarding and answer any questions
              along the way.
            </p>

            <div style={{ display: "flex", gap: 12 }}>
              {(Object.keys(REPS) as RepChoice[]).map((rep) => {
                const isSelected = selectedRep === rep;
                return (
                  <button
                    key={rep}
                    type="button"
                    onClick={() => handlePickRep(rep)}
                    style={{
                      flex: 1,
                      padding: "24px 16px",
                      border: `1.5px solid ${isSelected ? "#3045C9" : "rgba(13, 13, 18, 0.12)"}`,
                      borderRadius: 12,
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 12,
                      fontFamily: "inherit",
                      fontWeight: 600,
                      fontSize: 16,
                      color: "#0D0D12",
                      transition:
                        "border-color 0.15s ease, box-shadow 0.15s ease",
                      boxShadow: isSelected
                        ? "0 0 0 3px rgba(48, 69, 201, 0.12)"
                        : "none",
                    }}
                  >
                    <img
                      src={REPS[rep].avatarUrl}
                      alt=""
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: "50%",
                        objectFit: "cover",
                        background: "#F2F2EF",
                      }}
                    />
                    {REPS[rep].name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
